/**
 * Toucan Reads — what this browser was last looking at: the order the library
 * is in, and the list it is narrowed to.
 *
 * These are kept in `localStorage`. The synced store would give a phone and a
 * laptop the same view, though a person may like a different one on each. The
 * hash would put this browser's habits into a shared link, which should open
 * the library.
 *
 * The search box is deliberately not remembered. A search kept from yesterday
 * hides most of the library with nothing on screen saying why.
 */

import { rememberedSort } from "toucan";
import { DEFAULT_SORT, SORT_DOWNWARDS } from "./model.ts";
import type { ListFilter } from "toucan";
import type { ItemSortField, List } from "./model.ts";

const LIST_KEY = "reads-library-list";

/**
 * The item open in the reader. A share sheet relaunches the app over it, and
 * only what was written down before survives the reload.
 */
const READING_KEY = "reads-reading";

/** What the list key holds for the items on no list at all. */
const UNFILED = "unfiled";

/**
 * The order the library opens in, and where the one last chosen here is kept.
 * The valid fields are the keys of `SORT_DOWNWARDS`.
 */
const sort = rememberedSort(
  "reads-item-sort",
  Object.keys(SORT_DOWNWARDS) as ItemSortField[],
  DEFAULT_SORT,
);

export const storedSort = sort.stored;
export const rememberSort = sort.remember;

/** Note the item the reader is showing, or that it is showing none. */
export function rememberReading(id: string | undefined): void {
  if (id === undefined) {
    localStorage.removeItem(READING_KEY);
    return;
  }
  localStorage.setItem(READING_KEY, id);
}

/** The item the reader was showing when the page was last unloaded. */
export function storedReading(): string | undefined {
  return localStorage.getItem(READING_KEY) ?? undefined;
}

/** Remember which list the library is narrowed to, for the next visit. */
export function rememberList(listId: ListFilter): void {
  if (listId === null) {
    localStorage.removeItem(LIST_KEY);
    return;
  }
  localStorage.setItem(LIST_KEY, listId === "" ? UNFILED : listId);
}

/**
 * The list the library opens narrowed to, given the lists there are. A list
 * deleted on another device leaves its id stored here, and opens as the whole
 * library rather than an empty one with no chip pressed.
 */
export function storedList(lists: readonly List[]): ListFilter {
  const stored = localStorage.getItem(LIST_KEY);
  if (stored === null) {
    return null;
  }
  if (stored === UNFILED) {
    return "";
  }
  return lists.some((list) => list.id === stored) ? stored : null;
}
